export function canShowExplorerMergeAction(entity, isSteward = false) {
  if (!isSteward) return false;
  const entityId = String(entity?.id || "").trim();
  const entityType = String(entity?.type || entity?.entity_type || "").trim();
  const mergedIntoId = String(entity?.merged_into_id || "").trim();
  return Boolean(entityId && entityType && !mergedIntoId);
}

export function getExplorerMergeEntityType(entity) {
  return String(entity?.type || entity?.entity_type || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function normalizeMergeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function parseAliasNames(value) {
  return String(value || "")
    .replace(/;/g, ",")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function tokenSet(...values) {
  return new Set(
    values
      .flatMap((value) => normalizeMergeText(value).split(" "))
      .map((token) => token.trim())
      .filter((token) => token.length > 1)
  );
}

function overlapScore(left, right, multiplier) {
  if (!left.size || !right.size) return 0;
  let matches = 0;
  for (const token of left) {
    if (right.has(token)) matches += 1;
  }
  return Math.round((matches / Math.max(left.size, right.size)) * multiplier);
}

export function getExplorerMergeSimilarityScore(source, candidate) {
  if (!source || !candidate) return 0;

  const sourceName = normalizeMergeText(source?.name);
  const candidateName = normalizeMergeText(candidate?.name);
  const sourceAliases = parseAliasNames(source?.alias_names).map(normalizeMergeText);
  const candidateAliases = parseAliasNames(candidate?.alias_names).map(normalizeMergeText);
  const sourceNameTokens = tokenSet(source?.name);
  const candidateNameTokens = tokenSet(candidate?.name);
  const sourceContextTokens = tokenSet(
    source?.name,
    source?.description,
    ...(parseAliasNames(source?.alias_names) || [])
  );
  const candidateContextTokens = tokenSet(
    candidate?.name,
    candidate?.description,
    ...(parseAliasNames(candidate?.alias_names) || [])
  );

  let score = 0;

  if (sourceName && candidateName) {
    if (sourceName === candidateName) score += 140;
    else if (candidateName.startsWith(sourceName) || sourceName.startsWith(candidateName)) score += 90;
    else if (candidateName.includes(sourceName) || sourceName.includes(candidateName)) score += 65;
  }

  if (sourceName && candidateAliases.includes(sourceName)) score += 110;
  if (candidateName && sourceAliases.includes(candidateName)) score += 80;

  for (const alias of sourceAliases) {
    if (!alias) continue;
    if (alias === candidateName) {
      score += 100;
      break;
    }
    if (candidateAliases.includes(alias)) {
      score += 55;
      break;
    }
  }

  score += overlapScore(sourceNameTokens, candidateNameTokens, 80);
  score += overlapScore(sourceContextTokens, candidateContextTokens, 45);

  return score;
}

export function isLikelyExplorerMergeCandidate(source, candidate) {
  return getExplorerMergeSimilarityScore(source, candidate) >= 60;
}

export function sortExplorerMergeCandidates(candidates, source) {
  const list = Array.isArray(candidates) ? [...candidates] : [];
  return list.sort((left, right) => {
    const scoreDiff =
      getExplorerMergeSimilarityScore(source, right) -
      getExplorerMergeSimilarityScore(source, left);
    if (scoreDiff !== 0) return scoreDiff;

    const leftApproval = String(left?.approval_status || "Approved").trim();
    const rightApproval = String(right?.approval_status || "Approved").trim();
    if (leftApproval !== rightApproval) {
      if (leftApproval === "Approved") return -1;
      if (rightApproval === "Approved") return 1;
    }

    return String(left?.name || "").localeCompare(String(right?.name || ""));
  });
}
